import { useShift } from "../contexts/ShiftContext";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";
import { Loader2, Clock, Coffee, LogIn, LogOut, Play } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../lib/api";

const STATUS_LABEL = {
  not_started: "Punch in",
  active: "Working",
  on_break: "On break",
  completed: "Shift done",
};

const STATUS_DOT = {
  not_started: "bg-zinc-400",
  active: "bg-emerald-500 animate-pulse",
  on_break: "bg-amber-500",
  completed: "bg-zinc-300",
};

export default function ShiftWidget({ compact = true }) {
  const { shift, loading, punchIn, punchOut, startBreak, endBreak, fmtDuration } =
    useShift();

  const status = shift?.status || "not_started";
  const net = shift?.net_minutes || 0;
  const breakMin = shift?.break_minutes || 0;

  const handle = async (fn, label) => {
    try {
      await fn();
      toast.success(label);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="shift-widget-trigger"
          className="flex items-center gap-2 px-3 h-9 rounded-full border border-border bg-white/80 hover:bg-white transition-colors text-sm"
        >
          <span className={`h-2 w-2 rounded-full ${STATUS_DOT[status]}`} />
          <span className="font-medium hidden sm:inline">
            {STATUS_LABEL[status]}
          </span>
          {(status === "active" || status === "on_break" || status === "completed") && (
            <span className="text-muted-foreground tabular-nums">
              {fmtDuration(net)}
            </span>
          )}
          <Clock className="h-3.5 w-3.5 text-muted-foreground sm:hidden" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0 overflow-hidden"
        data-testid="shift-widget-panel"
      >
        <div className="dp-gradient-bg p-4 text-white">
          <div className="text-xs uppercase tracking-widest opacity-80">
            Today's shift
          </div>
          <div className="mt-1 font-display text-2xl font-bold tabular-nums">
            {fmtDuration(net)}
          </div>
          <div className="text-xs opacity-90 mt-0.5">
            {STATUS_LABEL[status]} · {fmtDuration(breakMin)} on break
          </div>
        </div>

        <div className="p-4 space-y-2">
          {status === "not_started" && (
            <Button
              className="w-full dp-gradient-bg text-white hover:opacity-90"
              onClick={() => handle(punchIn, "Punched in")}
              disabled={loading}
              data-testid="punch-in-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <LogIn className="h-4 w-4 mr-1.5" /> Punch in
                </>
              )}
            </Button>
          )}

          {status === "active" && (
            <>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => handle(startBreak, "Break started")}
                disabled={loading}
                data-testid="start-break-btn"
              >
                <Coffee className="h-4 w-4 mr-1.5" /> Start break
              </Button>
              <Button
                variant="destructive"
                className="w-full"
                onClick={() => handle(punchOut, "Punched out")}
                disabled={loading}
                data-testid="punch-out-btn"
              >
                <LogOut className="h-4 w-4 mr-1.5" /> Punch out
              </Button>
            </>
          )}

          {status === "on_break" && (
            <>
              <Button
                className="w-full dp-gradient-bg text-white hover:opacity-90"
                onClick={() => handle(endBreak, "Back to work")}
                disabled={loading}
                data-testid="end-break-btn"
              >
                <Play className="h-4 w-4 mr-1.5" /> End break
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => handle(punchOut, "Punched out")}
                disabled={loading}
                data-testid="punch-out-from-break-btn"
              >
                <LogOut className="h-4 w-4 mr-1.5" /> Punch out
              </Button>
            </>
          )}

          {status === "completed" && (
            <div className="text-center text-sm text-muted-foreground py-3">
              Shift completed. See you tomorrow!
            </div>
          )}

          {!compact && shift?.punch_in_at && (
            <div className="text-xs text-muted-foreground pt-2 border-t border-border">
              Started at {new Date(shift.punch_in_at).toLocaleTimeString()}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
